import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LANGS, LANG_LABELS, LANG_FLAGS, detectLang } from "./i18n";

/**
 * Terms of Service — /terms
 *
 * Written in English (international standard for ToS). Localized
 * site copy lives in landing/i18n.js; the legal text itself stays
 * in one canonical language to avoid translation drift.
 *
 * Last updated: 2026-05-21 (iter43 — added in response to Telegram
 * catalog moderation requirement around third-party rights).
 *
 * If you change material clauses, bump LAST_UPDATED and add a
 * dated entry to the changelog at the bottom.
 */
const LAST_UPDATED = "21 May 2026";

export default function Terms() {
  const [lang, setLang] = useState(detectLang);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("clipra.lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  return (
    <div className="dark bg-background text-foreground min-h-screen" data-testid="terms-page">
      <Nav lang={lang} setLang={setLang} />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
          // legal · v1.0
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mt-4">
          Terms of Service
        </h1>
        <p className="text-sm text-muted-foreground font-mono mt-2">
          Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-10 space-y-10 text-[15px] leading-relaxed">
          <Section n="1" title="What Clipra is">
            <p>
              Clipra is a video-editing tool that operates as a Telegram bot
              and Mini App. Clipra trims, crops and reformats videos that
              users submit by link, into share-ready mobile clips. Clipra
              is not a content library, search engine, or media archive.
            </p>
          </Section>

          <Section n="2" title="Your responsibility for content rights">
            <p>
              <strong>You</strong>, the user, are solely responsible for
              ensuring that you own — or have the necessary licenses,
              permissions, or other legal rights to use — any video you
              submit to Clipra. By pasting a link or uploading a file you
              represent that:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>The content is yours, OR</li>
              <li>You have permission from the rights holder, OR</li>
              <li>Your use of the content is otherwise lawful in your jurisdiction
                  (e.g. fair use, public domain, Creative Commons compatible).</li>
            </ul>
            <p className="mt-3">
              You agree not to use Clipra to process content that infringes
              third-party intellectual property, privacy, or publicity rights.
            </p>
          </Section>

          <Section n="3" title="What Clipra processes">
            <p>
              Clipra acts only on links you submit. It does not crawl,
              index, recommend, or aggregate third-party content. Every
              processing job is initiated by you, in your active Telegram
              session, on a one-time basis.
            </p>
            <p className="mt-3">
              Clipra uses standard open-source tooling (such as <code className="font-mono text-xs bg-border px-1.5 py-0.5 rounded">yt-dlp</code> and{" "}
              <code className="font-mono text-xs bg-border px-1.5 py-0.5 rounded">ffmpeg</code>) to retrieve and transform the content you ask
              about, in the same way a desktop user would with their own
              tools. Clipra adds editing convenience; it does not
              circumvent paywalls, DRM, or platform access controls.
            </p>
          </Section>

          <Section n="4" title="Data retention (TTL)">
            <p>
              Clipra is a processing service, not storage. Processed files
              are retained only as long as needed to deliver them and to
              allow you to refine the same clip without re-downloading.
              After that, files are automatically removed:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li><strong>Free plan</strong>: source and output files are deleted after <strong>5 days</strong>.</li>
              <li><strong>Pro plan</strong>: source and output files are deleted after <strong>30 days</strong>.</li>
              <li>URL metadata cache (title, duration): up to 24 hours, no media stored.</li>
              <li>Anonymized usage analytics (counts only, no video): retained for product
                  health metrics — never includes the content itself.</li>
            </ul>
            <p className="mt-3">
              You may delete any processed clip from your library at any
              time, which removes the underlying file from storage on the
              next cleanup pass. No content remains beyond the retention
              window described above.
            </p>
          </Section>

          <Section n="5" title="Acceptable use">
            <p>You agree not to use Clipra to:</p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5">
              <li>Process content you do not have rights to use.</li>
              <li>Circumvent payment, geo-blocks, DRM, or other access controls
                  imposed by the original platform or rights holder.</li>
              <li>Process content depicting minors in any sexualized context,
                  non-consensual intimate imagery, or material that violates
                  applicable law.</li>
              <li>Abuse the service (e.g. automated bulk requests, denial-of-service,
                  resale of API output without permission).</li>
            </ul>
            <p className="mt-3">
              We may suspend or remove access to Clipra for users found
              in material breach of these terms, without notice.
            </p>
          </Section>

          <Section n="6" title="Takedown and rights-holder requests">
            <p>
              If you are a rights holder and believe content has been
              processed through Clipra in violation of your rights, please
              contact us at{" "}
              <a className="underline hover:text-tg-link" href="mailto:legal@clipra.pro">legal@clipra.pro</a>{" "}
              with the original URL, a description of the work, and
              evidence of your authority to act. We will review reports
              promptly. Because Clipra does not store a content library,
              the typical remedy is account-level action against a user
              found to repeatedly infringe.
            </p>
          </Section>

          <Section n="7" title="Payments">
            <p>
              Pro subscriptions and one-time payments are processed via
              Telegram Stars, TON, and USDT. Pricing, plan terms and refund
              policy are displayed inside the Mini App at purchase. Charges
              made through Telegram Stars are governed additionally by
              Telegram's own payment terms.
            </p>
          </Section>

          <Section n="8" title="Disclaimers">
            <p>
              Clipra is provided <strong>"as is" and "as available"</strong>,
              without warranties of any kind, express or implied. We do not
              warrant that the service will be uninterrupted, error-free,
              or suitable for any particular purpose. Source-platform
              changes (e.g. updates to YouTube, Instagram, TikTok, or
              others) may temporarily affect compatibility.
            </p>
            <p className="mt-3">
              To the maximum extent permitted by law, Clipra and its
              operators are not liable for any indirect, incidental,
              special or consequential damages arising from your use of
              the service.
            </p>
          </Section>

          <Section n="9" title="Changes to these terms">
            <p>
              We may update these terms occasionally. Material changes
              will be reflected by the "Last updated" date at the top of
              this page. Continued use of Clipra after a change indicates
              acceptance of the updated terms.
            </p>
          </Section>

          <Section n="10" title="Contact">
            <p>
              Questions about these terms or your account:{" "}
              <a className="underline hover:text-tg-link" href="mailto:hello@clipra.pro">hello@clipra.pro</a>
              <br />
              Rights-holder / takedown:{" "}
              <a className="underline hover:text-tg-link" href="mailto:legal@clipra.pro">legal@clipra.pro</a>
            </p>
          </Section>
        </div>

        <div className="mt-16 pt-6 border-t border-border text-xs font-mono text-muted-foreground">
          <Link to="/" className="hover:text-foreground" data-testid="back-to-home">← Back to clipra.pro</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section data-testid={`terms-section-${n}`}>
      <h2 className="font-display text-2xl tracking-tight mb-3 flex items-baseline gap-3">
        <span className="text-muted-foreground font-mono text-sm">{n.padStart(2, "0")}</span>
        <span>{title}</span>
      </h2>
      <div className="text-muted-foreground space-y-1">
        {children}
      </div>
    </section>
  );
}

// Minimal nav (no language-dependent strings — Terms is English-only,
// but the lang switcher still works for navigating back to the localized
// landing page).
function Nav({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <span className="font-display text-[2.4rem] tracking-tight lowercase leading-none">clipra</span>
          <img
            src="/logos/clipra_icon_invert.png"
            alt=""
            aria-hidden="true"
            className="h-12 w-auto"
          />
        </Link>
        <div className="relative">
          <button
            onClick={() => setOpen(!open)}
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.12em] border border-border hover:bg-border/40"
            data-testid="terms-lang-toggle"
          >
            {LANG_FLAGS[lang]} {lang.toUpperCase()}
          </button>
          {open && (
            <ul className="absolute right-0 mt-2 bg-background border border-border z-40">
              {LANGS.map((l) => (
                <li key={l}>
                  <button
                    onClick={() => { setLang(l); setOpen(false); }}
                    className="w-full text-left px-4 py-2 text-xs font-mono hover:bg-border/40 whitespace-nowrap"
                    data-testid={`terms-lang-${l}`}
                  >
                    {LANG_FLAGS[l]} {LANG_LABELS[l]}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </header>
  );
}
